import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Helmet } from 'react-helmet-async';
import { BRAND_NAME } from '../../routes';
function stripHtml(input) {
    return input.replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();
}
/**
 * Centralized SEO/social metadata for product pages. Emits <title>, meta description,
 * OpenGraph tags, and a JSON-LD `Product` schema. Every bespoke product page wraps
 * its root in this so link previews and search engines get consistent data.
 */ export default function ProductSeo({ product, canonicalUrl }) {
    const title = `${product.name} — ${BRAND_NAME}`;
    const description = stripHtml(product.description || product.details || `${product.name} — built in-house.`);
    const image = product.imageUrl;
    const url = canonicalUrl ?? (typeof window !== 'undefined' ? `${window.location.origin}/shop/${product.slug}` : `/shop/${product.slug}`);
    const availability = product.inStock ? 'https://schema.org/InStock' : product.leadTimeDays > 0 ? 'https://schema.org/PreOrder' : 'https://schema.org/OutOfStock';
    const jsonLd = {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: product.name,
        description,
        image: image || undefined,
        sku: product.id,
        brand: {
            '@type': 'Brand',
            name: BRAND_NAME
        },
        offers: {
            '@type': 'Offer',
            url,
            priceCurrency: 'USD',
            price: (Number(product.priceCents) / 100).toFixed(2),
            availability
        }
    };
    return /*#__PURE__*/ _jsxs(Helmet, {
        children: [
            /*#__PURE__*/ _jsx("title", {
                children: title
            }),
            /*#__PURE__*/ _jsx("meta", {
                name: "description",
                content: description
            }),
            /*#__PURE__*/ _jsx("link", {
                rel: "canonical",
                href: url
            }),
            /*#__PURE__*/ _jsx("meta", {
                property: "og:type",
                content: "product"
            }),
            /*#__PURE__*/ _jsx("meta", {
                property: "og:title",
                content: title
            }),
            /*#__PURE__*/ _jsx("meta", {
                property: "og:description",
                content: description
            }),
            /*#__PURE__*/ _jsx("meta", {
                property: "og:url",
                content: url
            }),
            image && /*#__PURE__*/ _jsx("meta", {
                property: "og:image",
                content: image
            }),
            /*#__PURE__*/ _jsx("meta", {
                name: "twitter:card",
                content: image ? 'summary_large_image' : 'summary'
            }),
            /*#__PURE__*/ _jsx("meta", {
                name: "twitter:title",
                content: title
            }),
            /*#__PURE__*/ _jsx("meta", {
                name: "twitter:description",
                content: description
            }),
            image && /*#__PURE__*/ _jsx("meta", {
                name: "twitter:image",
                content: image
            }),
            /*#__PURE__*/ _jsx("script", {
                type: "application/ld+json",
                children: JSON.stringify(jsonLd)
            })
        ]
    });
}
